import React, { useState, useEffect } from 'react';
import { getMarketData, saveMarketData } from '../../api';
import IconButton from '../AIAnalysisButton';

interface MarketDataProps {
  customerId: string | number;
}

interface MarketData {
  id?: number;
  customerId: string | number;
  organizationName: string;
  industry: string;
  sectors: string;
  activeCountries: string;
  keyCompetitors: string;
  targetMarket: string;
  marketPosition: string;
  keyProducts: string;
  uniqueSellingPoints: string;
  recentMarketChanges: string;
  lastUpdated?: string;
}

const MarketDataSettings: React.FC<MarketDataProps> = ({ customerId }) => {
  const [marketData, setMarketData] = useState<MarketData>({
    customerId,
    organizationName: '',
    industry: '',
    sectors: '',
    activeCountries: '',
    keyCompetitors: '',
    targetMarket: '',
    marketPosition: '',
    keyProducts: '',
    uniqueSellingPoints: '',
    recentMarketChanges: ''
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const fetchMarketData = async () => {
      try {
        setLoading(true);
        const data = await getMarketData(customerId);
        if (data) {
          setMarketData(data);
        }
      } catch (err) {
        console.error('Error fetching market data:', err);
        setError('Failed to load market data');
      } finally {
        setLoading(false);
      }
    };

    fetchMarketData();
  }, [customerId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setMarketData(prev => ({
      ...prev,
      [name]: value
    }));

    // Reset success message when user makes changes
    if (saveSuccess) {
      setSaveSuccess(false);
    }

    // Clear field-level error on change
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      // Client-side validation for required fields
      const errors: { [key: string]: string } = {};
      if (!marketData.organizationName?.trim()) {
        errors.organizationName = 'Organization Name is required';
      }
      if (!marketData.industry?.trim()) {
        errors.industry = 'Industry is required';
      }
      if (!marketData.activeCountries?.trim()) {
        errors.activeCountries = 'Active Countries is required';
      }
      setFieldErrors(errors);
      if (Object.keys(errors).length > 0) {
        setSaving(false);
        return;
      }

      await saveMarketData({
        ...marketData,
        customerId
      });

      setSaveSuccess(true);

      // Hide success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Error saving market data:', err);
      setError('Failed to save market data');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div>Loading market data settings...</div>;
  }



  return (
    <div style={{ maxWidth: '95%', marginBottom: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2>Organization Market Data</h2>
          <p style={{ color: '#666' }}>
            This information will be used to generate AI-powered market insights tailored to your organization.
          </p>
        </div>


      </div>

      {error && (
        <div style={{
          padding: 12,
          backgroundColor: '#ffebee',
          color: '#c62828',
          borderRadius: 4,
          marginBottom: 16
        }}>
          {error}
        </div>
      )}

      {saveSuccess && (
        <div style={{
          padding: 12,
          backgroundColor: '#e8f5e9',
          color: '#2e7d32',
          borderRadius: 4,
          marginBottom: 16
        }}>
          Market data saved successfully!
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
            Organization Name <span style={{ color: '#c62828' }}>*</span>
          </label>
          <input
            type="text"
            name="organizationName"
            value={marketData.organizationName}
            onChange={handleInputChange}
            style={{
              width: '90%',
              padding: 10,
              borderRadius: 4,
              border: fieldErrors.organizationName ? '1px solid #c62828' : '1px solid #ddd',
              fontSize: 14
            }}
          />
          {fieldErrors.organizationName && (
            <div style={{ color: '#c62828', fontSize: 12, marginTop: 6 }}>{fieldErrors.organizationName}</div>
          )}
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
            Industry <span style={{ color: '#c62828' }}>*</span>
          </label>
          <input
            type="text"
            name="industry"
            value={marketData.industry}
            onChange={handleInputChange}
            style={{
              width: '100%',
              padding: 10,
              borderRadius: 4,
              border: fieldErrors.industry ? '1px solid #c62828' : '1px solid #ddd',
              fontSize: 14
            }}
            placeholder="e.g. Technology, Healthcare, Finance"
          />
          {fieldErrors.industry && (
            <div style={{ color: '#c62828', fontSize: 12, marginTop: 6 }}>{fieldErrors.industry}</div>
          )}
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
          Sectors
        </label>
        <input
          type="text"
          name="sectors"
          value={marketData.sectors}
          onChange={handleInputChange}
          style={{
            width: '100%',
            padding: 10,
            borderRadius: 4,
            border: '1px solid #ddd',
            fontSize: 14
          }}
          placeholder="e.g. SaaS, Fintech, E-commerce (comma separated)"
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
          Active Countries <span style={{ color: '#c62828' }}>*</span>
        </label>
        <input
          type="text"
          name="activeCountries"
          value={marketData.activeCountries}
          onChange={handleInputChange}
          style={{
            width: '100%',
            padding: 10,
            borderRadius: 4,
            border: fieldErrors.activeCountries ? '1px solid #c62828' : '1px solid #ddd',
            fontSize: 14
          }}
          placeholder="e.g. USA, UK, Germany, Japan (comma separated)"
        />
        {fieldErrors.activeCountries && (
          <div style={{ color: '#c62828', fontSize: 12, marginTop: 6 }}>{fieldErrors.activeCountries}</div>
        )}
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
          Key Competitors
        </label>
        <input
          type="text"
          name="keyCompetitors"
          value={marketData.keyCompetitors}
          onChange={handleInputChange}
          style={{
            width: '100%',
            padding: 10,
            borderRadius: 4,
            border: '1px solid #ddd',
            fontSize: 14
          }}
          placeholder="e.g. Microsoft, Google, Amazon (comma separated)"
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
          Target Market
        </label>
        <input
          type="text"
          name="targetMarket"
          value={marketData.targetMarket}
          onChange={handleInputChange}
          style={{
            width: '100%',
            padding: 10,
            borderRadius: 4,
            border: '1px solid #ddd',
            fontSize: 14
          }}
          placeholder="e.g. Enterprise businesses, SMBs, Consumers"
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
          Market Position
        </label>
        <input
          type="text"
          name="marketPosition"
          value={marketData.marketPosition}
          onChange={handleInputChange}
          style={{
            width: '100%',
            padding: 10,
            borderRadius: 4,
            border: '1px solid #ddd',
            fontSize: 14
          }}
          placeholder="e.g. Market leader, Challenger, Niche player"
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
          Key Products/Services
        </label>
        <input
          type="text"
          name="keyProducts"
          value={marketData.keyProducts}
          onChange={handleInputChange}
          style={{
            width: '100%',
            padding: 10,
            borderRadius: 4,
            border: '1px solid #ddd',
            fontSize: 14
          }}
          placeholder="e.g. Cloud services, Analytics platform, Mobile apps"
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
          Unique Selling Points
        </label>
        <textarea
          name="uniqueSellingPoints"
          value={marketData.uniqueSellingPoints}
          onChange={handleInputChange}
          style={{
            width: '100%',
            padding: 10,
            borderRadius: 4,
            border: '1px solid #ddd',
            fontSize: 14,
            minHeight: 80,
            resize: 'vertical'
          }}
          placeholder="What makes your organization stand out from competitors?"
        />
      </div>

      <div style={{ marginBottom: 24 }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
          Recent Market Changes
        </label>
        <textarea
          name="recentMarketChanges"
          value={marketData.recentMarketChanges}
          onChange={handleInputChange}
          style={{
            width: '100%',
            padding: 10,
            borderRadius: 4,
            border: '1px solid #ddd',
            fontSize: 14,
            minHeight: 80,
            resize: 'vertical'
          }}
          placeholder="Any significant market shifts or changes in the last 6-12 months?"
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '10px 20px',
            background: 'linear-gradient(115deg, rgb(34, 34, 69) 25%, rgb(-77, 182, 159) 162%)',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.7 : 1,
            fontSize: 14,
            fontWeight: 500
          }}
        >
          {saving ? 'Saving...' : 'Save Market Data'}
        </button>
      </div>
    </div>
  );
};

export default MarketDataSettings;
